import React, { useEffect } from 'react';
import { CarouselItem } from './carousel';
import { useCarouselTouch } from '@lightning/hooks';
import { PlatformService } from '@lightning/services';
import { cn } from '@lightning/utils';

interface MobileCarouselItemProps {
  children?: React.ReactNode;
  onItemClick?: () => void;
  className?: string;
  preventClickOnSwipe?: boolean;
}

/**
 * A wrapper component for carousel items that properly handles
 * tap vs swipe interactions on mobile devices.
 * 
 * - Single tap navigates to the item's page/action
 * - Swipe navigates the carousel
 * - Prevents click handlers from interfering with carousel swipe navigation
 */
export const MobileCarouselItem = React.forwardRef<
  HTMLDivElement,
  MobileCarouselItemProps
>(({ children, onItemClick, className, preventClickOnSwipe = true }, ref) => {
  const { handlers, cleanup } = useCarouselTouch({
    onTap: onItemClick,
    threshold: 10, // Allow 10px of movement for a tap
    tapDuration: 200, // Max 200ms for a tap
  });

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Only apply touch handlers on native platforms
  const touchHandlers = PlatformService.isNative() && preventClickOnSwipe ? handlers : {};

  return (
    <CarouselItem ref={ref} className={className}>
      <div
        {...touchHandlers}
        className={cn(
          "h-full w-full",
          // Prevent text selection on mobile to avoid interference with touch
          PlatformService.isNative() && "select-none"
        )}
        // Prevent default click behavior on mobile when using touch handlers
        onClick={(e) => {
          if (PlatformService.isNative() && preventClickOnSwipe) {
            e.preventDefault();
            e.stopPropagation();
          } else if (!PlatformService.isNative() && onItemClick) {
            onItemClick();
          }
        }}
      >
        {children}
      </div>
    </CarouselItem>
  );
});

MobileCarouselItem.displayName = 'MobileCarouselItem';